import {
  Box,
  Button,
  IconButton,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { useState, useMemo } from 'react';
import { toMinutesAndSeconds } from '../../utils/projectExecutionLifecycle';
import VisibilityIcon from '@material-ui/icons/Visibility';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import UnfoldMore from '@material-ui/icons/UnfoldMore';
import DescriptionOutlined from '@material-ui/icons/DescriptionOutlined';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import StopIcon from '@material-ui/icons/Stop';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useRunLogs } from '../../hooks/useRunLogs';
import { RunLogsDialog } from './RunLogsDialog';

import type { IqeProject, TestRun } from '../../interfaces/ui';
import { formatDateTime } from '../../utils';
import Badge from '../../common/Badge';
import CopyIconButton from '../../common/CopyIconButton';
import { useStyles } from '../../styles';
import '../../styles/projectsTable.css';
import { WarningIcon } from '../../icons/WarningIcon';
import { isRunLogsDownloadEligible } from '../../utils/projectStatusChecks';

interface TestExecutionHistoryProps {
  apmNumber?: string;
  project?: IqeProject;
  runs: TestRun[] | undefined;
  onViewResults?: (projectId: string) => void;
}

type SortColumn =
  | 'startedAt'
  | 'status'
  | 'testCases'
  | 'passRate'
  | 'failed'
  | 'duration';
type SortDirection = 'asc' | 'desc' | null;

const RUN_ID_TRUNCATE_LENGTH = 6;

const formatStatusLabel = (status: string) =>
  status.toUpperCase().includes('STOP_TRIGGERED')
    ? 'Stopping'
    : status.toUpperCase().includes('STOPPED')
    ? 'Stopped'
    : status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

export default function TestExecutionHistory({
  apmNumber = '',
  project,
  runs,
  onViewResults,
}: TestExecutionHistoryProps) {
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [statusReasonPopover, setStatusReasonPopover] = useState<{
    anchorEl: HTMLElement;
    runId: string;
  } | null>(null);
  const classes = useStyles();
  const logs = useRunLogs(apmNumber, project?.id || '');
  const isCompact = useMediaQuery<Theme>(
    theme => theme.breakpoints.down('sm'),
    { noSsr: true },
  );
  const isNarrowDesktop = useMediaQuery<Theme>(
    theme => theme.breakpoints.down('lg'),
    { noSsr: true },
  );
  const isMobile = useMediaQuery('(max-width: 1023.95px)', {
    noSsr: true,
  });

  const isResponsiveTable = isCompact || isNarrowDesktop;

  const headerLabel = (full: string, short: string) =>
    isResponsiveTable ? short : full;

  const hasAnyPassedTests = Boolean(runs?.some(run => Number(run.passed) > 0));

  const normalizedProjectStatus = (project?.status || '').toUpperCase();
  const isProjectExecutionInProgress =
    normalizedProjectStatus === 'GENERATING' ||
    normalizedProjectStatus === 'EXECUTING' ||
    normalizedProjectStatus === 'EXECUTE_INPROGRESS' ||
    /^GENERATE.*_INPROGRESS$/.test(normalizedProjectStatus) ||
    /^EXECUTE.*_INPROGRESS$/.test(normalizedProjectStatus);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedRuns = useMemo(() => {
    if (!sortColumn || !sortDirection) {
      return runs || [];
    }

    return [...(runs || [])].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'startedAt':
          aValue = a.startedAt ? new Date(a.startedAt).getTime() : 0;
          bValue = b.startedAt ? new Date(b.startedAt).getTime() : 0;
          break;
        case 'status':
          aValue = a.status?.toLowerCase() || '';
          bValue = b.status?.toLowerCase() || '';
          break;
        case 'testCases':
          aValue = a.testCases || 0;
          bValue = b.testCases || 0;
          break;
        case 'passRate':
          aValue = a.passRate || 0;
          bValue = b.passRate || 0;
          break;
        case 'failed':
          aValue = a.failed || 0;
          bValue = b.failed || 0;
          break;
        case 'duration':
          aValue = a.duration || 0;
          bValue = b.duration || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [runs, sortColumn, sortDirection]);

  const paginatedRuns = sortedRuns.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const getRunMeta = (run: TestRun) => {
    const isCurrentRunningRun = Boolean(
      project?.transactionId &&
        project.transactionId === run.transactionId &&
        isProjectExecutionInProgress,
    );

    return {
      isCurrentRunningRun,
      shouldShowViewResults:
        Boolean(onViewResults) &&
        hasAnyPassedTests &&
        !isCurrentRunningRun &&
        Boolean(run.dashboardUrl),
      shouldShowSummaryReport: !isCurrentRunningRun && Boolean(run.reportUrl),
      statusLabel: isCurrentRunningRun
        ? 'Running'
        : formatStatusLabel(run.status),
    };
  };

  const getFailureReason = (run: TestRun) =>
    run.statusReason?.trim() ||
    'The status reason is not available for this run.';

  const getStatusIcon = (statusLabel: string) => {
    const normalized = statusLabel.toUpperCase();
    if (normalized.includes('STOP')) {
      return (
        <StopIcon
          style={{ color: 'var(--ec-stop-run-accent)', fontSize: 13 }}
        />
      );
    }
    if (normalized.includes('FAIL')) {
      return <WarningIcon color="var(--pc-status-failed-text, #991B1B)" />;
    }
    if (normalized.includes('SUCCESS')) {
      return <CheckCircleIcon />;
    }
    return undefined;
  };

  const renderInlineFailureReason = (run: TestRun, statusLabel: string) =>
    statusLabel.toUpperCase().includes('FAIL') ? (
      <Typography
        className={`${classes.pdRunStatusReasonInline} ${classes.pdRunCardStatusReason}`}
      >
        {getFailureReason(run)}
      </Typography>
    ) : null;

  const renderStatus = (
    run: TestRun,
    statusLabel: string,
    showInlineReason = true,
  ) => {
    const runId = run.transactionId || 'unknown';
    const popoverId = `run-status-reason-${runId}`;
    const isFailedRun = statusLabel.toUpperCase().includes('FAIL');
    const shouldShowStatusReason = isFailedRun || run.status === 'SUCCESSFUL';
    const failureReason = getFailureReason(run);
    const isPopoverOpen = statusReasonPopover?.runId === runId;

    return (
      <Box className={classes.pdRunStatusContainer}>
        <Box className={classes.pdRunStatusValue}>
          <Badge
            text={statusLabel}
            className="pd-table-status-badge"
            icon={getStatusIcon(statusLabel)}
          />
          {!isMobile && (
            <Box className={classes.pdRunStatusReasonSlot}>
              {shouldShowStatusReason && (
                <>
              <Tooltip title="View status reason">
                <IconButton
                  aria-describedby={isPopoverOpen ? popoverId : undefined}
                  aria-label="View status reason"
                  className={classes.pdRunStatusReasonTrigger}
                  onClick={event =>
                    setStatusReasonPopover({
                      anchorEl: event.currentTarget,
                      runId,
                    })
                  }
                  size="small"
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Popover
                id={popoverId}
                open={isPopoverOpen}
                anchorEl={isPopoverOpen ? statusReasonPopover?.anchorEl : null}
                onClose={() => setStatusReasonPopover(null)}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                PaperProps={{
                  className: classes.pdRunStatusReasonPopoverPaper,
                }}
              >
                <Typography className={classes.pdRunStatusReasonText}>
                  {failureReason}
                </Typography>
              </Popover>
                </>
              )}
            </Box>
          )}
        </Box>
        {isFailedRun && isMobile && showInlineReason && (
          <Typography className={classes.pdRunStatusReasonInline}>
            {failureReason}
          </Typography>
        )}
      </Box>
    );
  };

  const renderViewResultsAction = (run: TestRun) =>
    isMobile ? (
      <Button
        variant="text"
        size="small"
        href={run.dashboardUrl || ''}
        startIcon={<VisibilityIcon />}
        color="primary"
        target="_blank"
        rel="noopener noreferrer"
        className="pd-view-results-btn pd-run-card-results-btn"
      >
        Test Results
      </Button>
    ) : isResponsiveTable ? (
      <Tooltip title="View test results">
        <IconButton
          size="small"
          color="primary"
          href={run.dashboardUrl || ''}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="View test results"
          className="pd-view-results-icon-btn"
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : (
      <Button
        variant="text"
        size="small"
        href={run.dashboardUrl || ''}
        startIcon={<VisibilityIcon />}
        color="primary"
        target="_blank"
        rel="noopener noreferrer"
        className="pd-view-results-btn"
      >
        Test Results
      </Button>
    );

  const renderSummaryReportAction = (run: TestRun) =>
    isMobile ? (
      <Button
        variant="text"
        size="small"
        href={run.reportUrl || ''}
        startIcon={<DescriptionOutlined />}
        color="primary"
        target="_blank"
        rel="noopener noreferrer"
        className="pd-view-results-btn"
      >
        Summary Report
      </Button>
    ) : isResponsiveTable ? (
      <Tooltip title="Summary Report">
        <IconButton
          size="small"
          color="primary"
          href={run.reportUrl || ''}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Summary Report"
          className="pd-view-results-icon-btn"
        >
          <DescriptionOutlined fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : (
      <Button
        variant="text"
        size="small"
        href={run.reportUrl || ''}
        startIcon={<DescriptionOutlined />}
        color="primary"
        target="_blank"
        rel="noopener noreferrer"
        className="pd-view-results-btn"
      >
        Summary Report
      </Button>
    );

  const renderDownloadLogsAction = (run: TestRun) => {
    if (
      !apmNumber ||
      !project?.id ||
      !run.transactionId ||
      !isRunLogsDownloadEligible(run.status.toUpperCase()) ||
      getRunMeta(run).isCurrentRunningRun
    )
      return null;

    const loading = logs.pendingRunId === run.transactionId;
    const disabled = Boolean(logs.pendingRunId) || logs.saving;
    const label = loading
      ? 'Loading logs...'
      : isMobile
      ? 'Download Logs'
      : 'Download';
    const onDownload = () => {
      void logs.fetchLogs(run.transactionId);
    };
    return !isMobile && isResponsiveTable ? (
      <Tooltip title={loading ? label : 'Download logs'}>
        <span>
          <IconButton
            size="small"
            color="primary"
            aria-label={`Download logs for run ${run.transactionId}`}
            className="pd-view-results-icon-btn"
            disabled={disabled}
            onClick={onDownload}
          >
            <GetAppIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    ) : (
      <Button
        variant="text"
        size="small"
        color="primary"
        startIcon={<GetAppIcon />}
        className="pd-view-results-btn"
        disabled={disabled}
        onClick={onDownload}
        aria-label={`Download logs for run ${run.transactionId}`}
      >
        {label}
      </Button>
    );
  };

  const renderRunActions = (
    run: TestRun,
    shouldShowViewResults: boolean,
    shouldShowSummaryReport: boolean,
  ) => (
    <Box className="pd-run-history-actions">
      {shouldShowViewResults && renderViewResultsAction(run)}
      {shouldShowSummaryReport && renderSummaryReportAction(run)}
      {renderDownloadLogsAction(run)}
    </Box>
  );

  const renderRunCards = () => (
    <Box className="pd-run-card-list">
      {paginatedRuns.map((run, index) => {
        const { shouldShowViewResults, shouldShowSummaryReport, statusLabel } =
          getRunMeta(run);
        const normalizedStatusLabel = statusLabel.toUpperCase();
        const cardStatusClass = normalizedStatusLabel.includes('STOP')
          ? classes.pdRunCardStopped
          : normalizedStatusLabel.includes('FAIL')
          ? classes.pdRunCardFailed
          : normalizedStatusLabel.includes('SUCCESS')
          ? classes.pdRunCardSuccessful
          : '';
        const failedMetricClass =
          run.failed > 0
            ? 'pd-run-card-metric-failed'
            : 'pd-run-card-metric-neutral';
        const runKey = `${run.transactionId}-${index}`;

        return (
          <Box key={runKey} className={`pd-run-card ${cardStatusClass}`}>
            <Box className="pd-run-card-header">
              <Box className="pd-run-card-id">
                <Typography
                  variant="body2"
                  className="pd-run-card-value pd-run-card-value-id"
                >
                  {`${(run.transactionId || '').slice(
                    0,
                    RUN_ID_TRUNCATE_LENGTH,
                  )}…`}
                </Typography>
                <CopyIconButton
                  dataToCopy={run.transactionId}
                  label="Copy run ID"
                  size="small"
                />
              </Box>
              <Typography variant="caption" className="pd-run-card-date">
                {formatDateTime(run.startedAt)}
              </Typography>
            </Box>

            <Box className="pd-run-card-operation">
              <Typography
                variant="body2"
                className={`${classes.pdRunCardOperationLabel} pd-run-card-value`}
              >
                {run.operation}
              </Typography>
              {renderStatus(run, statusLabel, false)}
            </Box>
            {renderInlineFailureReason(run, statusLabel)}

            <Box className="pd-run-card-stats">
              <Typography
                variant="body2"
                className="pd-run-card-value pd-run-card-metric"
              >
                <span className="pd-run-card-metric-label">Cases:</span>{' '}
                <span className="pd-run-card-metric-value">
                  {run.testCases}
                </span>
              </Typography>
              <Typography
                variant="body2"
                className="pd-run-card-value pd-run-card-metric pd-run-card-metric-pass"
              >
                <span className="pd-run-card-metric-label">Pass %:</span>{' '}
                <span className="pd-run-card-metric-value">
                  {run.passRate}%
                </span>
              </Typography>
              <Typography
                variant="body2"
                className={`pd-run-card-value pd-run-card-metric ${failedMetricClass}`}
              >
                <span className="pd-run-card-metric-label">Fail:</span>{' '}
                <span className="pd-run-card-metric-value">{run.failed}</span>
              </Typography>
              <Typography
                variant="body2"
                className="pd-run-card-value pd-run-card-metric pd-run-card-metric-skip"
              >
                <span className="pd-run-card-metric-label">Skip:</span>{' '}
                <span className="pd-run-card-metric-value">
                  {run.skipped ?? '__'}
                </span>
              </Typography>
            </Box>

            <Box className="pd-run-card-duration">
              <Typography variant="body2" className="pd-run-card-value">
                Duration:{' '}
                {run.duration ? toMinutesAndSeconds(run.duration) : 'N/A'}
              </Typography>
            </Box>

            <Box className="pd-run-card-footer">
              {renderRunActions(
                run,
                shouldShowViewResults,
                shouldShowSummaryReport,
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box className="pd-exec-history">
      <Typography variant="body1" className="pd-section-title">
        Execution history
      </Typography>
      <TableContainer component={Paper} className="projects-table-container">
        {isMobile ? (
          renderRunCards()
        ) : (
          <Table
            size="small"
            className="pd-table"
            aria-label="Execution history"
          >
            <TableHead>
              <TableRow classes={{ root: 'configTableRow' }}>
                <TableCell
                  classes={{ root: 'tableHeadCell' }}
                  className="pd-run-id-cell"
                >
                  <Typography variant="subtitle2">
                    {headerLabel('Run ID', 'Run')}
                  </Typography>
                </TableCell>
                <TableCell classes={{ root: 'tableHeadCell' }}>
                  <TableSortLabel
                    onClick={() => handleSort('startedAt')}
                    hideSortIcon={true}
                  >
                    <Box className="pd-sort-label">
                      <Typography variant="subtitle2">
                        {headerLabel('Execution date', 'Date')}
                      </Typography>
                      {sortColumn === 'startedAt' ? (
                        <ArrowUpward
                          className={`pd-sort-icon ${
                            sortDirection === 'desc' ? 'pd-sort-icon-desc' : ''
                          }`}
                        />
                      ) : (
                        <UnfoldMore className="pd-sort-icon pd-sort-icon-inactive" />
                      )}
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell classes={{ root: 'tableHeadCell' }}>
                  <Typography variant="subtitle2">Operation</Typography>
                </TableCell>
                <TableCell classes={{ root: 'tableHeadCell' }}>
                  <TableSortLabel
                    onClick={() => handleSort('status')}
                    hideSortIcon={true}
                  >
                    <Box className="pd-sort-label">
                      <Typography variant="subtitle2">Status</Typography>
                      {sortColumn === 'status' ? (
                        <ArrowUpward
                          className={`pd-sort-icon ${
                            sortDirection === 'desc' ? 'pd-sort-icon-desc' : ''
                          }`}
                        />
                      ) : (
                        <UnfoldMore className="pd-sort-icon pd-sort-icon-inactive" />
                      )}
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell classes={{ root: 'tableHeadCell' }}>
                  <TableSortLabel
                    onClick={() => handleSort('testCases')}
                    hideSortIcon={true}
                  >
                    <Box className="pd-sort-label">
                      <Typography variant="subtitle2">
                        {headerLabel('Test cases', 'Cases')}
                      </Typography>
                      {sortColumn === 'testCases' ? (
                        <ArrowUpward
                          className={`pd-sort-icon ${
                            sortDirection === 'desc' ? 'pd-sort-icon-desc' : ''
                          }`}
                        />
                      ) : (
                        <UnfoldMore className="pd-sort-icon pd-sort-icon-inactive" />
                      )}
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell classes={{ root: 'tableHeadCell' }}>
                  <TableSortLabel
                    onClick={() => handleSort('passRate')}
                    hideSortIcon={true}
                  >
                    <Box className="pd-sort-label">
                      <Typography variant="subtitle2">
                        {headerLabel('Pass rate', 'Pass')}
                      </Typography>
                      {sortColumn === 'passRate' ? (
                        <ArrowUpward
                          className={`pd-sort-icon ${
                            sortDirection === 'desc' ? 'pd-sort-icon-desc' : ''
                          }`}
                        />
                      ) : (
                        <UnfoldMore className="pd-sort-icon pd-sort-icon-inactive" />
                      )}
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell classes={{ root: 'tableHeadCell' }}>
                  <TableSortLabel
                    onClick={() => handleSort('failed')}
                    hideSortIcon={true}
                  >
                    <Box className="pd-sort-label">
                      <Typography variant="subtitle2">
                        {headerLabel('Failed', 'Fail')}
                      </Typography>
                      {sortColumn === 'failed' ? (
                        <ArrowUpward
                          className={`pd-sort-icon ${
                            sortDirection === 'desc' ? 'pd-sort-icon-desc' : ''
                          }`}
                        />
                      ) : (
                        <UnfoldMore className="pd-sort-icon pd-sort-icon-inactive" />
                      )}
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell classes={{ root: 'tableHeadCell' }}>
                  <Typography variant="subtitle2">
                    {headerLabel('Skipped', 'Skip')}
                  </Typography>
                </TableCell>
                <TableCell classes={{ root: 'tableHeadCell' }}>
                  <TableSortLabel
                    onClick={() => handleSort('duration')}
                    hideSortIcon={true}
                  >
                    <Box className="pd-sort-label">
                      <Typography variant="subtitle2">
                        {headerLabel('Duration', 'Dur')}
                      </Typography>
                      {sortColumn === 'duration' ? (
                        <ArrowUpward
                          className={`pd-sort-icon ${
                            sortDirection === 'desc' ? 'pd-sort-icon-desc' : ''
                          }`}
                        />
                      ) : (
                        <UnfoldMore className="pd-sort-icon pd-sort-icon-inactive" />
                      )}
                    </Box>
                  </TableSortLabel>
                </TableCell>
                <TableCell classes={{ root: 'tableHeadCell' }}>
                  <Typography variant="subtitle2">Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRuns.map((run, index) => {
                const {
                  shouldShowViewResults,
                  shouldShowSummaryReport,
                  statusLabel,
                } = getRunMeta(run);

                return (
                  <TableRow key={index} classes={{ root: 'configTableRow' }}>
                    <TableCell
                      classes={{ root: 'tableBodyCell' }}
                      className="pd-run-id-cell"
                    >
                      {isResponsiveTable ? (
                        <Box className="pd-runid-cell">
                          <Tooltip title={run.transactionId}>
                            <span className="pd-truncated-text">
                              {`${(run.transactionId || '').slice(
                                0,
                                RUN_ID_TRUNCATE_LENGTH,
                              )}…`}
                            </span>
                          </Tooltip>
                          <CopyIconButton
                            dataToCopy={run.transactionId}
                            label="Copy run ID"
                            size="small"
                          />
                        </Box>
                      ) : (
                        run.transactionId
                      )}
                    </TableCell>
                    <TableCell classes={{ root: 'tableBodyCell' }}>
                      {formatDateTime(run.startedAt)}
                    </TableCell>
                    <TableCell classes={{ root: 'tableBodyCell' }}>
                      {isResponsiveTable ? (
                        <Tooltip title={run.operation}>
                          <span className="pd-truncated-text">
                            {String(run.operation).split('_')[0]}
                          </span>
                        </Tooltip>
                      ) : (
                        run.operation
                      )}
                    </TableCell>
                    <TableCell classes={{ root: 'tableBodyCell' }}>
                      {renderStatus(run, statusLabel)}
                    </TableCell>
                    <TableCell classes={{ root: 'tableBodyCell' }}>
                      {run.testCases}
                    </TableCell>
                    <TableCell classes={{ root: 'tableBodyCell' }}>
                      {run.passRate}%
                    </TableCell>
                    <TableCell classes={{ root: 'tableBodyCell' }}>
                      {run.failed}
                    </TableCell>
                    <TableCell classes={{ root: 'tableBodyCell' }}>
                      {run.skipped ?? '__'}
                    </TableCell>
                    <TableCell classes={{ root: 'tableBodyCell' }}>
                      {run.duration ? toMinutesAndSeconds(run.duration) : 'N/A'}
                    </TableCell>
                    <TableCell classes={{ root: 'tableBodyCell' }}>
                      {renderRunActions(
                        run,
                        shouldShowViewResults,
                        shouldShowSummaryReport,
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={sortedRuns.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
      {logs.dialog && (
        <RunLogsDialog
          runId={logs.dialog.runId}
          fileNames={logs.dialog.files.map(file => file.fileName)}
          selectedIndices={logs.selectedIndices}
          saving={logs.saving}
          onToggle={logs.toggleFile}
          onDownload={all => {
            void logs.downloadFiles(all);
          }}
          onClose={logs.closeDialog}
        />
      )}
    </Box>
  );
}
